// Thin fetch wrapper around the pi-ap-control HTTP API. Mirrors the
// route shapes documented in SPEC §"Package: pi-ap-control" and
// implemented in packages/pi-ap-control/src/server.ts. Verified
// hardware-in-the-loop against a real Pi; not unit-tested per the
// project's no-mocking-network posture.

import type { ScenarioConfigSnapshot } from '../core/types.js';

export interface ScenarioListItem extends ScenarioConfigSnapshot {
  readonly name: string;
  readonly description: string;
  /**
   * Scenario-defined broadcast SSID, used by SSID-shape scenarios. When
   * absent, the web app defaults to `soluu-${name}`. See Scenario in
   * packages/pi-ap-control/src/types.ts.
   */
  readonly ssidOverride?: string;
  /**
   * Scenario-defined WPA passphrase, used by field-reproduction scenarios.
   * When absent, the web app defaults to its standard AP_PASSWORD.
   */
  readonly passwordOverride?: string;
}

export interface HealthResponse {
  readonly status: 'ok';
  readonly version: string;
}

export interface ApActiveStatus {
  readonly status: 'active';
  readonly ssid: string;
  readonly scenario: string | null;
  readonly config: ScenarioConfigSnapshot & { readonly beaconInterval?: number };
  readonly startedAt: string;
}

export interface ApStoppedStatus {
  readonly status: 'stopped';
}

export type ApStatus = ApActiveStatus | ApStoppedStatus;

export interface ActivateRequest {
  readonly scenario: string;
  readonly ssid: string;
  readonly password: string;
}

/**
 * Request body for POST /ap/custom — one-off AP configurations not in the
 * named scenario catalog. Used by the web app's Expert mode for ad-hoc
 * band/channel/SSID/password combinations a tester wants to try.
 */
export interface CustomRequest {
  readonly ssid: string;
  readonly password: string;
  readonly country: string;
  readonly channel: number;
  readonly txPowerDbm: number;
  readonly ieee80211d: boolean;
  readonly beaconInterval?: number;
}

export class PiClientError extends Error {
  readonly httpStatus?: number;
  readonly stderr?: string;
  constructor(message: string, opts: { httpStatus?: number; stderr?: string } = {}) {
    super(message);
    this.name = 'PiClientError';
    if (opts.httpStatus !== undefined) this.httpStatus = opts.httpStatus;
    if (opts.stderr !== undefined) this.stderr = opts.stderr;
  }
}

export interface PiClient {
  health(signal?: AbortSignal): Promise<HealthResponse>;
  scenarios(signal?: AbortSignal): Promise<readonly ScenarioListItem[]>;
  apStatus(signal?: AbortSignal): Promise<ApStatus>;
  activate(req: ActivateRequest, signal?: AbortSignal): Promise<ApActiveStatus>;
  custom(req: CustomRequest, signal?: AbortSignal): Promise<ApActiveStatus>;
  stop(signal?: AbortSignal): Promise<ApStoppedStatus>;
}

/** Trim trailing slashes so URL joining stays predictable. */
export function normalizePiBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

export function createPiClient(baseUrl: string): PiClient {
  const base = normalizePiBaseUrl(baseUrl);

  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    body: unknown,
    signal: AbortSignal | undefined,
  ): Promise<T> {
    const init: RequestInit = { method, signal };
    if (body !== undefined) {
      init.headers = { 'content-type': 'application/json' };
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, init);
    } catch (cause) {
      const reason = cause instanceof Error ? cause.message : String(cause);
      throw new PiClientError(`Pi backend unreachable at ${base}${path}: ${reason}`);
    }
    const text = await res.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new PiClientError(
          `Pi backend returned non-JSON for ${path} (HTTP ${res.status})`,
          { httpStatus: res.status },
        );
      }
    }
    if (!res.ok) {
      const errMsg =
        (parsed && typeof parsed === 'object' && 'error' in parsed
          ? String((parsed as { error: unknown }).error)
          : `HTTP ${res.status}`) ?? `HTTP ${res.status}`;
      const stderr =
        parsed && typeof parsed === 'object' && 'stderr' in parsed
          ? String((parsed as { stderr: unknown }).stderr)
          : undefined;
      throw new PiClientError(errMsg, { httpStatus: res.status, stderr });
    }
    return parsed as T;
  }

  return {
    health: (signal) => call<HealthResponse>('GET', '/health', undefined, signal),
    scenarios: async (signal) => {
      const body = await call<{ scenarios: readonly ScenarioListItem[] }>(
        'GET',
        '/scenarios',
        undefined,
        signal,
      );
      return body.scenarios;
    },
    apStatus: (signal) => call<ApStatus>('GET', '/ap/status', undefined, signal),
    activate: (req, signal) => call<ApActiveStatus>('POST', '/ap/activate', req, signal),
    custom: (req, signal) => call<ApActiveStatus>('POST', '/ap/custom', req, signal),
    stop: (signal) => call<ApStoppedStatus>('POST', '/ap/stop', undefined, signal),
  };
}
